import { useState } from 'react'
import { brandText, brandTint } from '../components/Brand.js'
import { FilterBar } from '../components/FilterBar.js'
import { Icon } from '../components/Icon.js'
import { ItemCard } from '../components/ItemCard.js'
import { PageHeader } from '../components/PageHeader.js'
import { addDays, addMonths, long, month, monthEnd, monthStart, parse, today, weekStart } from '../lib/dates.js'
import { toQuery, useFilters, useSheetParam } from '../lib/filters.js'
import { useItems } from '../lib/items.js'
import type { Item } from '../lib/items.js'

const WEEKDAYS = ['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So']

/** Month view by release day. A click on an empty day starts a new entry on that day. */
export function Calendar(): JSX.Element {
  const t = today()
  const [anchor, setAnchor] = useState(monthStart(t))
  const { filters } = useFilters()
  const first = weekStart(anchor)
  const last = addDays(weekStart(monthEnd(anchor)), 6)
  const { data: items = [] } = useItems({ ...toQuery(filters, false), from: first, to: last })
  const { openItem, openNew } = useSheetParam()

  const byDay = new Map<string, Item[]>()
  for (const i of items) if (i.status !== 'verworfen') byDay.set(i.start, [...(byDay.get(i.start) ?? []), i])

  const days: string[] = []
  for (let d = first; d <= last; d = addDays(d, 1)) days.push(d)

  return (
    <div className="mx-auto max-w-7xl space-y-4 px-5 py-6 md:px-8">
      <PageHeader
        title={month(anchor)}
        subtitle="nach Ausspieltag"
        action={
          <div className="flex items-center gap-1">
            <button type="button" className="icon-action" aria-label="Vorheriger Monat" onClick={() => setAnchor(addMonths(anchor, -1))}>
              <Icon name="chevronLeft" size={18} />
            </button>
            <button type="button" className="icon-action px-3 text-small font-medium" onClick={() => setAnchor(monthStart(t))}>
              Heute
            </button>
            <button type="button" className="icon-action" aria-label="Nächster Monat" onClick={() => setAnchor(addMonths(anchor, 1))}>
              <Icon name="chevronRight" size={18} />
            </button>
          </div>
        }
      />
      <FilterBar period={false} />

      {/* Phone: the month as an agenda — seven columns leave three letters per title at 375 pt. */}
      <div className="space-y-4 md:hidden">
        {days
          .filter((d) => d.slice(0, 7) === anchor.slice(0, 7) && (byDay.has(d) || d === t))
          .map((d) => (
            <section key={d} className="flex gap-3">
              <button
                type="button"
                onClick={() => openNew(d)}
                aria-label={`Neuer Eintrag am ${long(d)}`}
                className={`w-12 shrink-0 pt-1 text-center ${d === t ? 'text-pebro' : 'text-label'}`}
              >
                <span className="block text-caption font-semibold uppercase text-label2/50">{WEEKDAYS[(parse(d).getDay() + 6) % 7]}</span>
                <span className="num font-brand text-h3 font-light leading-none">{parse(d).getDate()}</span>
              </button>
              <div className="min-w-0 flex-1 space-y-2">
                {(byDay.get(d) ?? []).map((i) => (
                  <ItemCard key={i.id} item={i} onOpen={() => openItem(i.id)} />
                ))}
                {!byDay.has(d) && <p className="pt-2 text-small text-label2/45">Heute nichts geplant.</p>}
              </div>
            </section>
          ))}
        {![...byDay.keys()].some((d) => d.slice(0, 7) === anchor.slice(0, 7)) && (
          <p className="card text-center text-small text-label2/60">In diesem Monat ist nichts geplant.</p>
        )}
      </div>

      <div className="hidden overflow-hidden rounded-card bg-cell md:block">
        <div className="grid grid-cols-7 border-b border-sep/10">
          {WEEKDAYS.map((w) => (
            <div key={w} className="py-2 text-center text-caption font-semibold text-label2/50">
              {w}
            </div>
          ))}
        </div>
        <div className="grid grid-cols-7">
          {days.map((d) => {
            const inMonth = d.slice(0, 7) === anchor.slice(0, 7)
            const list = byDay.get(d) ?? []
            const weekend = parse(d).getDay() % 6 === 0
            return (
              <div
                key={d}
                onClick={(e) => {
                  if (e.target === e.currentTarget) openNew(d)
                }}
                className={`group min-h-[5.5rem] cursor-pointer border-b border-r border-sep/10 p-1 md:min-h-[7.5rem] md:p-1.5 ${
                  inMonth ? (weekend ? 'bg-fill/[0.03]' : '') : 'bg-fill/[0.06] opacity-50'
                }`}
              >
                <div className="pointer-events-none flex items-center justify-between px-1">
                  <span
                    className={`num grid h-6 min-w-6 place-items-center rounded-full text-caption ${
                      d === t ? 'bg-pebro px-1.5 font-semibold text-white' : 'text-label2/70'
                    }`}
                  >
                    {parse(d).getDate()}
                  </span>
                  <span className="text-label2/0 transition-colors group-hover:text-label2/40">+</span>
                </div>
                <div className="mt-1 space-y-1">
                  {list.slice(0, 4).map((i) => (
                    <button
                      key={i.id}
                      type="button"
                      onClick={() => openItem(i.id)}
                      className="flex w-full items-center gap-1 truncate rounded-[6px] px-1.5 py-0.5 text-left text-[11px] font-medium leading-tight md:text-caption"
                      style={{ backgroundColor: brandTint(i.brand, 14), color: brandText(i.brand) }}
                      title={i.title}
                    >
                      {i.status === 'veroeffentlicht' && <Icon name="check" size={11} />}
                      <span className="truncate">{i.title}</span>
                    </button>
                  ))}
                  {list.length > 4 && <p className="px-1.5 text-[11px] text-label2/60">+{list.length - 4} weitere</p>}
                </div>
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
